import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid2,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import RedirectIcon from "../../../Components/Icons/RedirectIcon";
import { Link } from "react-router-dom";
import { getInventoryFilterUrl } from "../CommonFunctions";

export default function NonOperationalAccordion({
  title,
  depots = [],
  showBorder = true,
  globalFilters,
}) {
  const [expanded, setExpanded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const getCountDisplay = (count) => {
    if (count > 1) {
      return `${count} Buses`;
    }
    if (count === 1) {
      return `${count} Bus`;
    }
    return '-';
  };

  useEffect(() => {
    const total = depots.reduce((sum, depot) => sum + (depot.count || 0), 0);
    setTotalCount(total);
  }, [depots]);

  const isDisabled = totalCount === 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => !isDisabled && setExpanded((prev) => !prev)}
      disableGutters
      elevation={0}
      square
      sx={{
        "&:before": { display: "none" },
        borderBottom: showBorder ? "1px solid #E4E4E4" : 'none',
        ...(isDisabled && { pointerEvents: 'none' }),
        "&.Mui-disabled": {
          backgroundColor: '#FFFFFF',
        },
      }}
    >
      <AccordionSummary
        sx={{
          "& .MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            m: 0,
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <ExpandMoreIcon
            fontSize="large"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              "&:hover": {
                color: isDisabled ? '#C8D1DA' : '#4D44D8',
              },
              color: isDisabled ? '#C8D1DA' : '#000000',
            }}
          />
          <Typography
            variant="fw500s14c000000"
            sx={{ lineHeight: '20px', color: isDisabled ? '#686868' : '#000000' }}
          >
            {title}
          </Typography>
        </Box>

        <Link
          to={getInventoryFilterUrl(globalFilters, { tag_battery_status: ["Non-Operational"], tag_battery_sub_status: [title] })}
          style={{display: 'contents', textDecoration: 'none'}}
          target="_blank"
        >
          <PrimaryTooltip title="View in Inventory">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                color: isDisabled ? '#686868' : '#6D72F6',
                '&:hover': { color: isDisabled ? '#686868' : '#4D44D8' },
              }}
            >
              <Typography
                variant="fw500s14c494949"
                sx={{ lineHeight: '20px', color: 'inherit' }}
              >
                {getCountDisplay(totalCount)}
              </Typography>

              {totalCount > 0 && (
                <RedirectIcon sx={{ fontSize: 14, color: 'inherit' }} />
              )}
            </Box>
          </PrimaryTooltip>
        </Link>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: 2 }}>
        <Grid2 container flexDirection={'column'} gap={0.5}>
          {depots.map((depot) => (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1,
                pr: 0,
                pl: 4,
                ...(depot.count === 0 && { color: '#686868', pointerEvents: 'none' }),
              }}
            >
              <Typography
                variant="fw400s14c494949"
                sx={{ lineHeight: '20px', color: depot.count === 0 ? '#686868' : '#494949' }}
              >
                {depot.label}
              </Typography>
              <Link
                to={getInventoryFilterUrl(globalFilters, {
                  tag_battery_status: ["Non-Operational"],
                  tag_battery_sub_status: [title],
                  ...(!["Depot Not Set", "Not Set"].includes(depot.label) && { originDepo: [depot.label] }),
                })}
                style={{display: 'contents', textDecoration: 'none'}}
                target="_blank"
              >
                <PrimaryTooltip title="View in Inventory">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: depot.count > 0 ? 'pointer' : 'default',
                      color: depot.count === 0 ? '#686868' : '#494949',
                      '&:hover': { color: depot.count > 0 ? '#6D72F6' : '#686868' },
                    }}
                  >
                    <Typography
                      variant="fw500s14c494949"
                      sx={{ lineHeight: '20px', color: 'inherit' }}
                    >
                      {getCountDisplay(depot.count)}
                    </Typography>

                    {depot.count > 0 && (
                      <RedirectIcon sx={{ fontSize: 14, color: 'inherit' }} />
                    )}
                  </Box>
                </PrimaryTooltip>
              </Link>
            </Box>
          ))}
        </Grid2>
      </AccordionDetails>
    </Accordion>
  );
}
